import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext';
import { Web3Provider } from './contexts/Web3Context';
import { NetworkProvider } from './contexts/NetworkContext';
import ProtectedRoute from './auth/ProtectedRoute';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import TrustExplorer from './pages/TrustExplorer';
import MyDIDs from './pages/MyDIDs';
import AIAgents from './pages/AIAgents';
import Verifications from './pages/Verifications';
import AgentMarketplace from './pages/AgentMarketplace';
import Schemas from './pages/Schemas';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import OAuthCallback from './pages/auth/OAuthCallback';

// Docs (public)
import Docs from './pages/Docs';

// DID Pages
import DIDDetail from './pages/DIDDetail';
import DIDResolver from './pages/DIDResolver';

// Agent Detail Pages
import AgentDetail from './pages/agents/AgentDetail';
import DeployAgent from './pages/agents/DeployAgent';
import AgentToolsPage from './pages/agents/AgentToolsPage';
import AgentAuditPage from './pages/agents/AgentAuditPage';
import AgentPoliciesPage from './pages/agents/AgentPoliciesPage';

// Issuer Portal
import IssuerOnboarding from './pages/issuer/IssuerOnboarding';
import SchemaManagement from './pages/issuer/SchemaManagement';
import CredentialIssuance from './pages/issuer/CredentialIssuance';
import BulkIssuance from './pages/issuer/BulkIssuance';
import RevocationManagement from './pages/issuer/RevocationManagement';
import IssuerAnalytics from './pages/issuer/IssuerAnalytics';

// ZK Identity
import ZKIdentity from './pages/ZKIdentity';

// Verifier Portal
import VerifierOnboarding from './pages/verifier/VerifierOnboarding';
import ProofRequestBuilder from './pages/verifier/ProofRequestBuilder';
import VerificationEngine from './pages/verifier/VerificationEngine';
import VerificationHistory from './pages/verifier/VerificationHistory';
import TrustFramework from './pages/verifier/TrustFramework';

// Holder mirror page (Phase 7-C — PEx v2 web fallback for the wallet)
import RespondToRequest from './pages/credentials/RespondToRequest';
import MyCredentials from './pages/credentials/MyCredentials';

// Phase 7-B — Marketplace v2
import ListingDetail from './pages/marketplace/ListingDetail';
import MyListings from './pages/marketplace/MyListings';
import MyEscrows from './pages/marketplace/MyEscrows';
import Earnings from './pages/marketplace/Earnings';
import IssueAttestation from './pages/verifier/IssueAttestation';

// Phase 7-F.2 — Adjudicator console (skeleton; full UX deferred)
import AdjudicatorDashboardScreen from './pages/adjudicator/AdjudicatorDashboardScreen';
import RoleApplicationPage from './pages/RoleApplicationPage';
import AdminRoleApplicationsPage from './pages/admin/AdminRoleApplicationsPage';
import AdminUserDirectory from './pages/admin/AdminUserDirectory';
import Settings from './pages/Settings';

// IDA-UPI: mandates, payments and bank instruments
import UPIPayments from './pages/upi/UPIPayments';

export default function App() {
  return (
    <NetworkProvider>
    <Web3Provider>
    <AuthProvider>
      <Routes>
        {/* Public auth routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/callback" element={<OAuthCallback />} />
        <Route path="/docs" element={<Docs />} />

        {/* Protected routes */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout><Dashboard /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/trust-explorer"
          element={
            <ProtectedRoute>
              <Layout><TrustExplorer /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-dids"
          element={
            <ProtectedRoute>
              <Layout><MyDIDs /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-dids/:did"
          element={
            <ProtectedRoute>
              <Layout><DIDDetail /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/resolve"
          element={
            <ProtectedRoute>
              <Layout><DIDResolver /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-agents"
          element={
            <ProtectedRoute>
              <Layout><AIAgents /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-agents/deploy"
          element={
            <ProtectedRoute>
              <Layout><DeployAgent /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-agents/:did"
          element={
            <ProtectedRoute>
              <Layout><AgentDetail /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agents/:did/tools"
          element={
            <ProtectedRoute>
              <Layout><AgentToolsPage /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agents/:did/audit"
          element={
            <ProtectedRoute>
              <Layout><AgentAuditPage /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agents/:did/policies"
          element={
            <ProtectedRoute>
              <Layout><AgentPoliciesPage /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/upi"
          element={
            <ProtectedRoute>
              <Layout><UPIPayments /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/verifications"
          element={
            <ProtectedRoute>
              <Layout><Verifications /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agent-marketplace"
          element={
            <ProtectedRoute>
              <Layout><AgentMarketplace /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/schemas"
          element={
            <ProtectedRoute>
              <Layout><Schemas /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/zk-identity"
          element={
            <ProtectedRoute>
              <Layout><ZKIdentity /></Layout>
            </ProtectedRoute>
          }
        />

        {/* Issuer Portal */}
        <Route
          path="/issuer/onboarding"
          element={
            <ProtectedRoute requiredRole="issuer">
              <Layout><IssuerOnboarding /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/issuer/schemas"
          element={
            <ProtectedRoute requiredRole="issuer">
              <Layout><SchemaManagement /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/issuer/issue"
          element={
            <ProtectedRoute requiredRole="issuer">
              <Layout><CredentialIssuance /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/issuer/bulk-issue"
          element={
            <ProtectedRoute requiredRole="issuer">
              <Layout><BulkIssuance /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/issuer/revocations"
          element={
            <ProtectedRoute requiredRole="issuer">
              <Layout><RevocationManagement /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/issuer/analytics"
          element={
            <ProtectedRoute requiredRole="issuer">
              <Layout><IssuerAnalytics /></Layout>
            </ProtectedRoute>
          }
        />

        {/* Verifier Portal */}
        <Route
          path="/verifier/onboarding"
          element={
            <ProtectedRoute requiredRole="verifier">
              <Layout><VerifierOnboarding /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/verifier/proof-request"
          element={
            <ProtectedRoute requiredRole="verifier">
              <Layout><ProofRequestBuilder /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/verifier/verify"
          element={
            <ProtectedRoute requiredRole="verifier">
              <Layout><VerificationEngine /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/verifier/history"
          element={
            <ProtectedRoute requiredRole="verifier">
              <Layout><VerificationHistory /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/verifier/trust-framework"
          element={
            <ProtectedRoute requiredRole="verifier">
              <Layout><TrustFramework /></Layout>
            </ProtectedRoute>
          }
        />

        {/* Holder — credentials issued to the current user */}
        <Route
          path="/my-credentials"
          element={
            <ProtectedRoute>
              <Layout><MyCredentials /></Layout>
            </ProtectedRoute>
          }
        />

        {/* Holder mirror page — desktop fallback when the wallet isn't available. */}
        <Route
          path="/credentials/respond/:requestId"
          element={
            <ProtectedRoute>
              <Layout><RespondToRequest /></Layout>
            </ProtectedRoute>
          }
        />

        {/* Phase 7-B — Marketplace v2 + Tokenised Reputation */}
        <Route
          path="/marketplace/listings/:id"
          element={
            <ProtectedRoute>
              <Layout><ListingDetail /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/operators/me/listings"
          element={
            <ProtectedRoute>
              <Layout><MyListings /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/operators/me/escrows"
          element={
            <ProtectedRoute>
              <Layout><MyEscrows /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/operators/me/earnings"
          element={
            <ProtectedRoute>
              <Layout><Earnings /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/verifier/attestations/issue"
          element={
            <ProtectedRoute requiredRole="verifier">
              <Layout><IssueAttestation /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/adjudicator"
          element={
            <ProtectedRoute>
              <Layout><AdjudicatorDashboardScreen /></Layout>
            </ProtectedRoute>
          }
        />

        {/* Role upgrade application (holders) + Admin review panel */}
        <Route
          path="/role-application"
          element={
            <ProtectedRoute>
              <Layout><RoleApplicationPage /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/role-applications"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout><AdminRoleApplicationsPage /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users/:role"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout><AdminUserDirectory /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Layout><Settings /></Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </AuthProvider>
    </Web3Provider>
    </NetworkProvider>
  );
}
